import {
  Bar,
  CartesianGrid,
  Cell,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useChartColors } from "./chart-theme";

export interface BarChartProps {
  /** Chart data array */
  data: Array<Record<string, unknown>>;
  /** Key for the bar value */
  dataKey: string;
  /** Key for the category label */
  nameKey: string;
  /** Chart layout: horizontal bars or vertical bars */
  layout?: "horizontal" | "vertical";
  /** Optional per-cell color function (receives datum, index) */
  colorFn?: (datum: Record<string, unknown>, index: number) => string;
  /** Chart height in pixels */
  height?: number;
}

/**
 * Themed Recharts BarChart component.
 * Supports both horizontal and vertical layouts with SLATE colors.
 */
export function BarChart({
  data,
  dataKey,
  nameKey,
  layout = "vertical",
  colorFn,
  height = 300,
}: BarChartProps) {
  const colors = useChartColors();

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart
        data={data}
        layout={layout}
        margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={colors.grid}
          horizontal={layout === "vertical"}
          vertical={layout === "horizontal"}
        />
        {layout === "vertical" ? (
          <>
            <XAxis type="number" stroke={colors.axis} tick={{ fill: colors.text, fontSize: 12 }} />
            <YAxis
              type="category"
              dataKey={nameKey}
              stroke={colors.axis}
              tick={{ fill: colors.text, fontSize: 12 }}
              width={100}
            />
          </>
        ) : (
          <>
            <XAxis
              type="category"
              dataKey={nameKey}
              stroke={colors.axis}
              tick={{ fill: colors.text, fontSize: 12 }}
            />
            <YAxis type="number" stroke={colors.axis} tick={{ fill: colors.text, fontSize: 12 }} />
          </>
        )}
        <Tooltip
          contentStyle={{
            backgroundColor: colors.surface,
            border: `1px solid ${colors.grid}`,
            borderRadius: "8px",
            color: colors.text,
          }}
          cursor={{ fill: colors.primaryLight, opacity: 0.3 }}
        />
        <Bar dataKey={dataKey} fill={colors.primary} radius={4}>
          {colorFn &&
            // biome-ignore lint/suspicious/noArrayIndexKey: chart cells have no stable key
            data.map((datum, index) => <Cell key={`cell-${index}`} fill={colorFn(datum, index)} />)}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
